import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { formatBillingOfferAmount, getTopUpOffers } from "@/features/billing/billingOffers";
import type { BillingOffer, BillingOfferCode } from "@/features/billing/billing.types";

type TopUpPanelProps = {
  canTopUp: boolean;
  onCheckout: (offerCode: BillingOfferCode) => void;
  offers: BillingOffer[];
};

export function TopUpPanel({ canTopUp, onCheckout, offers }: TopUpPanelProps) {
  const { i18n, t } = useTranslation();
  const topUpOffers = getTopUpOffers(offers);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-ink/78">
          {t("billing.topUps.eyebrow")}
        </p>
        <h3 className="text-[2rem] font-semibold leading-tight text-ink">
          {t("billing.topUps.title")}
        </h3>
        <p className="max-w-3xl text-sm leading-7 text-ink/62">
          {canTopUp ? t("billing.topUps.body") : t("billing.topUps.freePrompt")}
        </p>
      </div>

      <div className="divide-y divide-[#ece2d6] border-y border-[#ece2d6]">
        {topUpOffers.map((offer) => (
          <div
            key={offer.offerCode}
            className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between"
          >
            <div>
              <p className="text-[1.05rem] font-semibold text-ink">
                {t("billing.topUps.dynamicTitle", { count: offer.creditAmount })}
              </p>
              <p className="mt-1 text-sm text-ink/62">
                {t("billing.topUps.dynamicDescription")}
              </p>
            </div>

            <Button
              className="h-14 min-w-[5.25rem] rounded-[1rem] border border-[#e0d4c5] bg-white px-5 text-[1.05rem] font-semibold text-ink shadow-none hover:bg-[#fffaf4]"
              disabled={!canTopUp}
              onClick={() => onCheckout(offer.offerCode)}
              type="button"
              variant="outline"
            >
              {canTopUp
                ? formatBillingOfferAmount(offer, i18n.resolvedLanguage ?? i18n.language)
                : t("billing.topUps.upgrade")}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
